import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import Button from 'flarum/common/components/Button';
import { sectionMissingSubsection } from './subsectionRule';
import { sections } from './utils/sections';

/**
 * Composer: one system for replying, starting a discussion and editing — plain-language labels,
 * placeholders and submit buttons, errors shown next to the field, a clear "publishing…" state.
 * Core's composer, editor, drafts and validation stay as they are; on phones the layout
 * (less/forum/composer.less) turns the floating composer (new discussion, editing) into a
 * full-screen form with a labelled submit button. Replies are written in the page (inlineReply.tsx).
 */
const key = k => `tarotportal-theme.forum.composer.${k}`;
const text = k => app.translator.trans(key(k), {}, true);
const kindOf = attrs => attrs.post ? 'edit' : attrs.discussion ? 'reply' : 'discussion';

/** What is missing in the composer body, once the user has tried to submit it. */
function problems(body) {
  if (!body.tpTried) return {};
  const kind = kindOf(body.attrs);
  const found = {};
  const content = String(body.composer.fields.content() || '').trim();
  if (kind === 'discussion') {
    const title = String(body.title?.() || '').trim();
    if (!title) found.title = text('error_title');else if (title.length < 3) found.title = text('error_title_short');

    // flarum/tags: a section (and its subsection, theme rule) is required whenever sections exist
    if (body.composer.fields.tags !== undefined && sections().length) {
      const chosen = body.composer.fields.tags || [];
      if (!chosen.length) found.tags = text('error_tags');else if (sectionMissingSubsection(chosen)) found.tags = text('error_subsection');
    }
  }
  if (!content) found.content = text(kind === 'reply' ? 'error_reply' : 'error_body');
  return found;
}
const errorLine = message => message ? m("p", {
  className: "TpFieldError",
  role: "alert"
}, message) : null;

/**
 * The visible part of the screen, for the full-screen forms on phones (composer, section picker):
 * with the on-screen keyboard open the layout viewport keeps its height on iOS, so these forms
 * follow the visual viewport instead (CSS reads --tp-vv-height / --tp-vv-top; 100dvh is the
 * fallback). While the keyboard is open <html> has .tp-keyboard-open (compact bottom bar), and the
 * focused field of the composer is brought into the visible part of its one scroll — by the
 * smallest scroll that does it, never by scrolling the page.
 */
function trackVisualViewport() {
  const viewport = window.visualViewport;
  if (!viewport) return;
  const root = document.documentElement;
  const sync = () => {
    root.style.setProperty('--tp-vv-height', `${Math.round(viewport.height)}px`);
    root.style.setProperty('--tp-vv-top', `${Math.round(viewport.offsetTop)}px`);
    root.classList.toggle('tp-keyboard-open', window.innerHeight - viewport.height > 120);
  };
  let frame = 0;
  const update = () => {
    sync();
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(revealFocusedField);
  };
  viewport.addEventListener('resize', update);
  viewport.addEventListener('scroll', sync);
  sync();

  // a field just focused: once the keyboard has settled
  document.addEventListener('focusin', e => {
    if (e.target.closest?.('#composer .Composer')) setTimeout(revealFocusedField, 320);
  });
}

/** Smallest scroll of the composer's form that shows the focused field above the toolbar/submit. */
function revealFocusedField() {
  const field = document.activeElement;
  const composer = field?.closest('#composer .Composer');
  if (!field || !composer || !field.matches('input, textarea')) return;
  const scroller = composer.querySelector('.ComposerBody-content');
  const controls = composer.querySelector('.TextEditor-controls');
  if (!scroller || !controls || scroller.scrollHeight <= scroller.clientHeight) return;
  const area = scroller.getBoundingClientRect();
  const top = area.top + 8;
  const bottom = Math.min(area.bottom, controls.getBoundingClientRect().top) - 8;
  const rect = field.getBoundingClientRect();
  let delta = 0;
  if (rect.top < top) delta = rect.top - top;else if (rect.bottom > bottom) delta = Math.min(rect.bottom - bottom, rect.top - top); // a tall editor: its top stays in view

  if (delta) scroller.scrollTop += delta;
}
export default function addComposer() {
  trackVisualViewport();

  // Placeholders and submit labels for the kind of message being written (core fills in its own
  // defaults only where none are given).
  override('flarum/forum/components/ComposerBody', 'setAttrs', function (original, attrs) {
    if (attrs === void 0) {
      attrs = {};
    }
    const kind = kindOf(attrs);
    if (kind === 'reply') {
      attrs.placeholder ??= text('reply_placeholder');
      attrs.submitLabel ??= text('reply_submit');
    } else if (kind === 'discussion') {
      attrs.placeholder ??= text('discussion_placeholder');
      attrs.titlePlaceholder ??= text('title_placeholder');
      attrs.submitLabel ??= text('discussion_submit');
    }
    return original(attrs);
  });

  // Full-screen form (phones): its top bar "× НОВАЯ ТЕМА" stays put while the form scrolls under it.
  // The bar is part of core's composer shell; the × is core's own control placed in it (CSS).
  override('flarum/forum/components/Composer', 'view', function (original) {
    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }
    const vnode = original(...args);
    const body = this.state.body;
    if (vnode && Array.isArray(vnode.children) && body?.componentClass && !body.attrs?.tpInline) {
      vnode.children.splice(2, 0, m("div", {
        className: "TpComposerBar"
      }, text(`${kindOf(body.attrs)}_kind`)));
    }
    return vnode;
  });

  // Head of the form: what is being written ("Ответ на тему" + the topic), field labels and
  // their errors. Labels and the kind line are shown on phones (CSS); errors everywhere.
  extend('flarum/forum/components/ComposerBody', 'headerItems', function (items) {
    const kind = kindOf(this.attrs);
    const found = problems(this);
    items.add('tp-kind', m("p", {
      className: "TpComposerKind"
    }, text(`${kind}_kind`)), 1000);
    if (kind === 'discussion') {
      // in reading order: section (flarum/tags' picker, 10), then title (core's input, 0)
      items.add('tp-label-tags', m("p", {
        className: "TpComposerLabel"
      }, text('tags_label')), 20);
      items.add('tp-label-title', m("p", {
        className: "TpComposerLabel"
      }, text('title_label')), 5);
      items.add('tp-label-body', m("p", {
        className: "TpComposerLabel"
      }, text('body_label')), -900);

      // each error right after its field
      if (found.tags) items.add('tp-error-tags', errorLine(found.tags), 8);
      if (found.title) items.add('tp-error-title', errorLine(found.title), -1);
    }
    if (found.content) items.add('tp-error-content', errorLine(found.content), -1000);
  });

  // Toolbar (flarum/markdown): no "Вставить изображение" — it only types the ![](https://) template;
  // pictures come from "Мои загрузки" (fof/upload). Markdown image syntax itself keeps working.
  extend('flarum/common/components/TextEditor', 'markdownToolbarItems', function (items) {
    if (items.has('image')) items.remove('image');
  });

  // An empty section field says what to choose (flarum/tags' generic "choose tags" otherwise)
  extend('flarum/forum/components/DiscussionComposer', 'headerItems', function (items) {
    if (!items.has('tags') || (this.composer.fields.tags || []).length) return;
    const button = items.get('tags');
    if (button && typeof button === 'object') {
      button.children = [m("span", {
        className: "TagLabel untagged"
      }, text('choose_section'))];
    }
  });

  // Nothing is sent while something required is missing (the errors above say what), nor a
  // second time while the first submission is on its way.
  for (const component of ['ReplyComposer', 'DiscussionComposer', 'EditPostComposer']) {
    override(`flarum/forum/components/${component}`, 'onsubmit', function (original) {
      if (this.loading) return;
      this.tpTried = true;
      if (Object.keys(problems(this)).length) {
        m.redraw();
        return;
      }
      for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
        args[_key2 - 1] = arguments[_key2];
      }
      return original(...args);
    });
  }

  // The submit button says what it does and, while the server works, that it is working; it
  // cannot be pressed twice (core disables the editor during that time; the button follows it).
  extend('flarum/common/components/TextEditor', 'controlItems', function (items) {
    if (!items.has('submit') || !this.attrs.disabled) return;
    const kind = kindOf(app.composer.body?.attrs || {});
    items.setContent('submit', m(Button, {
      className: "Button Button--primary",
      icon: "fas fa-paper-plane",
      itemClassName: "App-primaryControl",
      disabled: true,
      loading: true
    }, text(`${kind}_loading`)));
  });
}